import { useGeneralSettingsFormData } from "@/api/cms";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import type { Selection } from "@/lib/cms/section-types";
import { DEFAULT_PLAYER_SETTINGS } from "@/lib/media/embed";
import { cn } from "@/lib/utils";

type SelectionItem = Selection["item"];

type SelectionMediaFields = {
  kind?: string;
  url?: string;
  videoPublicUrl?: string | null;
  posterPublicUrl?: string | null;
  embedProvider?: string;
  embedVideoId?: string;
  embedUrl?: string;
  videoLoop?: boolean;
};

function mediaFields(item: SelectionItem): SelectionMediaFields {
  return item as SelectionItem & SelectionMediaFields;
}

/**
 * Média d'un item de sélection (image / vidéo / embed) — parité frontpublic
 * `GridDisplay`. Sans ça, les vidéos importées ne montraient que le poster
 * (`url` = vignette côté API).
 */
export function SelectionItemMedia({
  item,
  className,
  imgRef,
  onImgLoad,
  draggable = false,
}: {
  item: SelectionItem;
  className?: string;
  imgRef?: React.Ref<HTMLImageElement>;
  onImgLoad?: () => void;
  draggable?: boolean;
}) {
  const { data: playerSettings = DEFAULT_PLAYER_SETTINGS } =
    useGeneralSettingsFormData();
  const media = mediaFields(item);
  const kind = media.kind ?? "image";

  if (kind === "video") {
    if (media.videoPublicUrl) {
      return (
        <ResponsiveVideo
          className={cn("relative w-full", className)}
          src={media.videoPublicUrl}
          poster={media.posterPublicUrl ?? media.url ?? undefined}
          loop={playerSettings.playerLoop}
        />
      );
    }
    // Conversion en cours / erreur : fallback vignette si dispo.
    if (media.url || media.posterPublicUrl) {
      return (
        <img
          ref={imgRef}
          className={cn("relative w-full", className)}
          src={media.posterPublicUrl ?? media.url}
          alt=""
          draggable={draggable}
          onLoad={onImgLoad}
        />
      );
    }
    return null;
  }

  if (kind === "embed") {
    return (
      <EmbedPlayer
        className={cn("relative w-full", className)}
        provider={media.embedProvider}
        videoId={media.embedVideoId}
        embedUrl={media.embedUrl}
        videoLoop={media.videoLoop}
        title="embed"
      />
    );
  }

  return (
    <img
      ref={imgRef}
      className={cn("relative w-full", className)}
      src={media.url ?? ""}
      alt=""
      draggable={draggable}
      onLoad={onImgLoad}
    />
  );
}
